/**
 * API client.
 *
 * The session is a HttpOnly cookie the browser sends automatically; the CSRF
 * token is the readable companion cookie, echoed back in a header on every write
 * (§6). No token is ever kept in localStorage, and no secret is bundled into the
 * frontend (§17).
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  idempotencyKey?: string;
  /** Return the raw Response instead of parsed JSON (CSV export, audio preview). */
  raw?: boolean;
}

export async function api<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {};

  if (!['GET', 'HEAD'].includes(method)) {
    const csrf = readCookie('sa_csrf');
    if (csrf) headers['x-csrf-token'] = csrf;
  }
  if (options.idempotencyKey) headers['idempotency-key'] = options.idempotencyKey;

  let body: BodyInit | undefined;
  if (options.body instanceof FormData) {
    body = options.body;
  } else if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  const res = await fetch(`/api/v1${path}`, {
    method,
    headers,
    body,
    credentials: 'same-origin',
    signal: options.signal,
  });

  if (options.raw) {
    if (!res.ok) throw await toError(res);
    return res as unknown as T;
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const isJson = res.headers.get('content-type')?.includes('json');
  const parsed = isJson && text ? JSON.parse(text) : text;

  if (!res.ok) {
    const err = parsed as { code?: string; message?: string; details?: unknown; request_id?: string };
    throw new ApiError(
      res.status,
      err?.code ?? 'unknown',
      err?.message ?? `فشل الطلب (${res.status})`,
      err?.details,
      err?.request_id,
    );
  }
  return parsed as T;
}

async function toError(res: Response): Promise<ApiError> {
  let payload: { code?: string; message?: string; request_id?: string } = {};
  try {
    payload = await res.json();
  } catch {
    /* non-JSON error body */
  }
  return new ApiError(
    res.status,
    payload.code ?? 'unknown',
    payload.message ?? `فشل الطلب (${res.status})`,
    undefined,
    payload.request_id,
  );
}

export const get = <T>(path: string) => api<T>(path);
export const post = <T>(path: string, body?: unknown, idempotencyKey?: string) =>
  api<T>(path, { method: 'POST', body, idempotencyKey });
export const patch = <T>(path: string, body?: unknown) => api<T>(path, { method: 'PATCH', body });
export const put = <T>(path: string, body?: unknown) => api<T>(path, { method: 'PUT', body });
export const del = <T>(path: string) => api<T>(path, { method: 'DELETE' });

/** Build a query string, dropping empty values so the URL stays clean. */
export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

/** Trigger a CSV download without ever putting credentials into a URL. */
export async function downloadCsv(path: string, filename: string): Promise<void> {
  const res = await api<Response>(path, { raw: true });
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
